import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Checkbox, InputLabel, Select as MuiSelect, Tooltip } from "@mui/material";
import { FormattedMessage, injectIntl } from "react-intl";
import MenuItem from "@mui/material/MenuItem";
import { SearchInput } from "app/components";
import Chip from "@mui/material/Chip";
import Dropdown from "./Dropdown";
import { HasAccessTo } from "app/components/Authorization";
import { CONNECTION, EVENT } from "app/common/Authorization/entities";
import { DOWNLOAD, EDIT } from "app/common/Authorization/permissions";
import {
  DatePickerStyled,
  DivRowStyled,
  DropdownStyled,
  FormControlStyled,
  ResetButtonStyled,
  SelectStyled,
  SpacerStyled
} from "./EventsToolbar.styles";
import { TranslationKeys } from "../../../../translations";
import { useConnectionEventTypeSelectItems } from "../../../../hooks/useConnectionEventTypeSelectItems";
const BTN_PROCESS_TESTID = "btn-process";
const BTN_PROCESS_ALL_TESTID = "btn-process-all";
const BTN_PROCESS_PENSIOENBIJ_TESTID = "btn-process-pensioenbij";
const BTN_PROCESS_PENSIOENBIJ_ALL_TESTID = "btn-process-pensioenbij-all";
const BTN_ARCHIVE_TESTID = "btn-archive";
const BTN_ARCHIVE_ALL_TESTID = "btn-archive-all";
const BTN_ARCHIVE_ALL_SAME_PERIOD_TESTID = "btn-archive-all-same-period";
const BTN_UNARCHIVE_TESTID = "btn-unarchive";
const BTN_UNARCHIVE_ALL_TESTID = "btn-unarchive-all";
const BTN_DOWNLOAD_TESTID = "btn-download";
const BTN_DOWNLOAD_ALL_TESTID = "btn-download-all";
const BTN_RESET_FILTERS_TESTID = "btn-reset-filters";
const EventsToolbar = (props) => {
  const {
    handleSearch,
    filters,
    filtersCheck,
    applyFilter,
    processEvents,
    processAllEvents,
    archiveEvents,
    archiveAllEvents,
    unArchiveEvents,
    unArchiveAllEvents,
    hasSelectedItems,
    allEventsArchived,
    allEventsUnarchived,
    resetFilters,
    downloadEvents,
    downloadAllEvents,
    intl,
    archiveSamePeriodEvents,
    initialSearchQuery,
    comparisonEvents,
    comparisonAllEvents,
    comparisonActive,
    hasComparisonActive,
    isPensioenbijConnection,
    processPensioenbijMutations,
    processAllPensioenbijMutations
  } = props;
  const filterEventDateAfter = filters["eventDate[after]"];
  const filterEventDateBefore = filters["eventDate[before]"];
  const archiveTooltip = () => !hasSelectedItems ? intl.formatMessage({ id: "tooltip_events_nothing_selected" }) : intl.formatMessage({ id: "tooltip_events_already_archived" });
  const unArchiveTooltip = () => !hasSelectedItems ? intl.formatMessage({ id: "tooltip_events_nothing_selected" }) : intl.formatMessage({ id: "tooltip_events_already_unarchived" });
  const disabledIfNoAccessToEventEdit = !HasAccessTo(EVENT, EDIT);
  const disabledIfNoAccessToConnectionDownload = !HasAccessTo(CONNECTION, DOWNLOAD);
  const { connectionEventTypes } = useConnectionEventTypeSelectItems();
  const kebabToCamel = (str) => str.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  const dropdownOptions = {
    /*draftBatch: [
      {
        title: intl.formatMessage({ id: TranslationKeys.events_draftBatch_createSelected }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: TranslationKeys.events_draftBatch_confirmDialog_title }),
          message: intl.formatMessage({ id: TranslationKeys.events_draftBatch_confirmDialog_message }),
        },
        action: () => createDraftBatch(false),
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys.events_draftBatch_createAll }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: TranslationKeys.events_draftBatch_confirmDialog_title }),
          message: intl.formatMessage({ id: TranslationKeys.events_draftBatch_confirmDialog_message }),
        },
        action: () => createDraftBatch(true),
        disabled: disabledIfNoAccessToEventEdit,
      },
    ],*/
    baselineMeasurement: [
      {
        title: intl.formatMessage({ id: "events.baseline.lastActiveAction.title" }),
        tooltip: intl.formatMessage({ id: "events.baseline.lastActiveAction.tooltip" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.baseline.lastActiveAction.confirm.title" }),
          message: intl.formatMessage({ id: "events.baseline.lastActiveAction.confirm.message" })
        },
        action: comparisonActive,
        disabled: disabledIfNoAccessToEventEdit || !hasComparisonActive
      },
      {
        title: intl.formatMessage({ id: "events.baseline.selectedAction.title" }),
        tooltip: intl.formatMessage({ id: "events.baseline.selectedAction.tooltip" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.baseline.selectedAction.confirm.title" }),
          message: intl.formatMessage({ id: "events.baseline.selectedAction.confirm.message" })
        },
        action: comparisonEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems || hasComparisonActive
      },
      {
        title: intl.formatMessage({ id: "events.baseline.allAction.title" }),
        tooltip: intl.formatMessage({ id: "events.baseline.allAction.tooltip" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.baseline.allAction.confirm.title" }),
          message: intl.formatMessage({ id: "events.baseline.allAction.confirm.message" })
        },
        action: comparisonAllEvents,
        disabled: disabledIfNoAccessToEventEdit || hasComparisonActive
      }
    ],
    process: [
      {
        title: intl.formatMessage({ id: "events.processAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.processConfirmTitle" }),
          message: intl.formatMessage({ id: "events.processConfirmMessage" })
        },
        action: processEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems,
        testId: BTN_PROCESS_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.processAllAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.processConfirmTitle" }),
          message: intl.formatMessage({ id: "events.processAllConfirmMessage" })
        },
        action: processAllEvents,
        disabled: disabledIfNoAccessToEventEdit,
        testId: BTN_PROCESS_ALL_TESTID
      }
    ],
    processPensioenbij: [
      {
        title: intl.formatMessage({ id: "events.processPensioenbijAllAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.processConfirmTitle" }),
          message: intl.formatMessage({ id: "events.processAllConfirmMessage" })
        },
        action: processAllPensioenbijMutations,
        disabled: disabledIfNoAccessToEventEdit,
        testId: BTN_PROCESS_PENSIOENBIJ_ALL_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.processPensioenbijAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.processConfirmTitle" }),
          message: intl.formatMessage({ id: "events.processConfirmMessage" })
        },
        action: processPensioenbijMutations,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems,
        testId: BTN_PROCESS_PENSIOENBIJ_TESTID
      }
    ],
    archive: [
      {
        title: intl.formatMessage({ id: "events.archiveAction" }),
        tooltip: archiveTooltip(),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.archiveConfirmTitle" }),
          message: intl.formatMessage({ id: "events.archiveConfirmMessage" })
        },
        action: archiveEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems || !allEventsUnarchived,
        testId: BTN_ARCHIVE_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.archiveAllAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.archiveConfirmTitle" }),
          message: intl.formatMessage({ id: "events.archiveAllConfirmMessage" })
        },
        action: archiveAllEvents,
        disabled: disabledIfNoAccessToEventEdit,
        testId: BTN_ARCHIVE_ALL_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.archiveAllSamePeriodAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.archiveSamePeriodConfirmTitle" }),
          message: intl.formatMessage({ id: "events.archiveSamePeriodConfirmMessage" })
        },
        action: archiveSamePeriodEvents,
        disabled: disabledIfNoAccessToEventEdit,
        testId: BTN_ARCHIVE_ALL_SAME_PERIOD_TESTID
      }
    ],
    unArchive: [
      {
        title: intl.formatMessage({ id: "events.unArchiveAction" }),
        tooltip: unArchiveTooltip(),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.unArchiveConfirmTitle" }),
          message: intl.formatMessage({ id: "events.unArchiveConfirmMessage" })
        },
        action: unArchiveEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems || !allEventsArchived,
        testId: BTN_UNARCHIVE_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.unArchiveAllAction" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.unArchiveConfirmTitle" }),
          message: intl.formatMessage({ id: "events.unArchiveAllConfirmMessage" })
        },
        action: unArchiveAllEvents,
        disabled: disabledIfNoAccessToEventEdit,
        testId: BTN_UNARCHIVE_ALL_TESTID
      }
    ],
    download: [
      {
        title: intl.formatMessage({ id: "events.download" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.downloadConfirmTitle" }),
          message: intl.formatMessage({ id: "events.downloadConfirmMessage" })
        },
        action: downloadEvents,
        disabled: disabledIfNoAccessToConnectionDownload || !hasSelectedItems,
        testId: BTN_DOWNLOAD_TESTID
      },
      {
        title: intl.formatMessage({ id: "events.downloadAll" }),
        tooltip: intl.formatMessage({ id: "tooltip_events_nothing_selected" }),
        confirmDialog: {
          title: intl.formatMessage({ id: "events.downloadConfirmTitle" }),
          message: intl.formatMessage({ id: "events.downloadAllConfirmMessage" })
        },
        action: downloadAllEvents,
        disabled: disabledIfNoAccessToConnectionDownload,
        testId: BTN_DOWNLOAD_ALL_TESTID
      }
    ]
  };
  const handleFromDateChange = ({ value: date }) => {
    applyFilter("eventDate[after]", date);
  };
  const handleUntilDateChange = ({ value: date }) => {
    applyFilter("eventDate[before]", date);
  };
  const changeEventType = useCallback(
    (event) => {
      applyFilter("eventType", event.target.value ? event.target.value : void 0);
    },
    [applyFilter]
  );
  const changeArchived = useCallback(
    ({ value }) => {
      applyFilter("archived", value);
    },
    [applyFilter]
  );
  const changeCorrection = useCallback(
    ({ value }) => {
      applyFilter("correction", value);
    },
    [applyFilter]
  );
  const eventsFields = useMemo(() => {
    return {
      from: {
        type: "date",
        name: "from",
        label: intl.formatMessage({ id: "events.datePickerFrom" }),
        value: filterEventDateAfter ?? null,
        fullWidth: false,
        options: {
          startOfDay: true
        }
      },
      until: {
        type: "date",
        name: "until",
        label: intl.formatMessage({ id: "events.datePickerUntil" }),
        value: filterEventDateBefore ?? null,
        fullWidth: false,
        options: {
          endOfDay: true
        }
      },
      archived: {
        type: "select",
        name: "archived",
        label: intl.formatMessage({ id: "events.archived" }),
        fullWidth: false,
        value: filters.archived,
        displayEmptyOption: true,
        items: [
          {
            element: "all",
            value: ""
          },
          {
            element: "yes",
            value: true
          },
          {
            element: "no",
            value: false
          }
        ]
      },
      correction: {
        type: "select",
        name: "correction",
        label: intl.formatMessage({ id: "events.correction" }),
        fullWidth: false,
        value: filters.correction,
        displayEmptyOption: true,
        items: [
          {
            element: "all",
            value: null
          },
          {
            element: "yes",
            value: true
          },
          {
            element: "no",
            value: false
          }
        ]
      }
    };
  }, [filterEventDateAfter, filterEventDateBefore, filters.archived, filters.correction, intl]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DivRowStyled, null, /* @__PURE__ */ React.createElement(DropdownStyled, { options: dropdownOptions.baselineMeasurement, defaultSelectedIndex: 0, intl: true }), /* @__PURE__ */ React.createElement(DropdownStyled, { options: dropdownOptions.process, intl: true }), /* @__PURE__ */ React.createElement(DropdownStyled, { options: dropdownOptions.archive, intl: true }), /* @__PURE__ */ React.createElement(DropdownStyled, { options: dropdownOptions.unArchive }), isPensioenbijConnection && /* @__PURE__ */ React.createElement(DropdownStyled, { options: dropdownOptions.processPensioenbij, defaultSelectedIndex: 0, intl: true }), /* @__PURE__ */ React.createElement(Tooltip, { title: filtersCheck ? intl.formatMessage({ id: "tooltip_events_no_filters_applied" }) : "" }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(ResetButtonStyled, { onClick: resetFilters, disabled: filtersCheck, "data-testid": BTN_RESET_FILTERS_TESTID }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.resetFilters" })))), /* @__PURE__ */ React.createElement(SpacerStyled, null), /* @__PURE__ */ React.createElement(Dropdown, { options: dropdownOptions.download })), /* @__PURE__ */ React.createElement(DivRowStyled, null, /* @__PURE__ */ React.createElement(FormControlStyled, null, /* @__PURE__ */ React.createElement(InputLabel, null, intl.formatMessage({ id: TranslationKeys.global_eventType })), /* @__PURE__ */ React.createElement(
    MuiSelect,
    {
      multiple: true,
      value: filters.eventType ?? [],
      onChange: changeEventType,
      renderValue: (selected) => /* @__PURE__ */ React.createElement("div", null, selected.map((value) => /* @__PURE__ */ React.createElement(
        Chip,
        {
          key: value,
          label: intl.formatMessage({ id: TranslationKeys[`events_type_${kebabToCamel(value)}`] })
        }
      )))
    },
    connectionEventTypes.map((type) => /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        key: type.value,
        value: type.value,
        selected: filters.eventType ? filters.eventType.includes(type.value) : false
      },
      /* @__PURE__ */ React.createElement(
        Checkbox,
        {
          color: "secondary",
          checked: filters.eventType ? filters.eventType.includes(type.value) : false
        }
      ),
      type.element
    ))
  )), /* @__PURE__ */ React.createElement(
    SelectStyled,
    {
      key: `archived_${eventsFields.archived.value}`,
      field: eventsFields.archived,
      onChange: changeArchived
    }
  ), /* @__PURE__ */ React.createElement(
    DatePickerStyled,
    {
      key: `from_${eventsFields.from.value}`,
      field: eventsFields.from,
      onChange: handleFromDateChange
    }
  ), /* @__PURE__ */ React.createElement(
    DatePickerStyled,
    {
      key: `until_${eventsFields.until.value}`,
      field: eventsFields.until,
      onChange: handleUntilDateChange
    }
  ), /* @__PURE__ */ React.createElement(
    SelectStyled,
    {
      key: `archived_${eventsFields.correction.value}`,
      field: eventsFields.correction,
      onChange: changeCorrection
    }
  ), /* @__PURE__ */ React.createElement(SpacerStyled, null), /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      onChange: handleSearch,
      placeholder: intl.formatMessage({ id: "events.search" }),
      initialSearchQuery
    }
  )));
};
EventsToolbar.propTypes = {
  isPensioenbijConnection: PropTypes.bool,
  processPensioenbijMutations: PropTypes.func,
  processAllPensioenbijMutations: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};
export default injectIntl(EventsToolbar);
